import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  BarChart3, Building2, Users, Target, FileText, TrendingUp, Download,
  Activity, ArrowUp, ArrowDown,
} from 'lucide-react';

const PLAN_LABEL = {
  start: 'Start',
  growth: 'Growth',
  pro: 'Pro',
  scale: 'Scale',
  enterprise: 'Enterprise',
};

const CONTRACT_STATUS_BADGE = {
  aktivt: { text: 'Aktivt', bg: '#dcfce7', color: '#166534' },
  framtida: { text: 'Framtida', bg: '#dbeafe', color: '#1e40af' },
  utgånget: { text: 'Utgånget', bg: '#fee2e2', color: '#991b1b' },
  avslutat: { text: 'Avslutat', bg: '#fee2e2', color: '#991b1b' },
  utkast: { text: 'Utkast', bg: '#f3f4f6', color: '#374151' },
};

const formatSek = (n) => `${Math.round(n || 0).toLocaleString('sv-SE')} kr`;
const formatInt = (n) => (n ?? 0).toLocaleString('sv-SE');
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('sv-SE') : '—');

// ── CSV-export ────────────────────────────────────────────────────────────────

function downloadCsv(rows) {
  const headers = [
    'Kund', 'Org.nr', 'Status', 'Plan', 'Avtalsstatus', 'Avtal start',
    'Avtal slut', 'MRR (kr)', 'Användare', 'Deltagare', 'Skapad',
  ];
  const csv = [headers.join(';')];
  for (const r of rows) {
    csv.push([
      r.name,
      r.org_number || '',
      r.status || '',
      PLAN_LABEL[r.contract_package] || r.contract_package || '',
      r.contract_status || '',
      r.contract_start || '',
      r.contract_end || '',
      r.monthly_price || 0,
      r.user_count,
      r.participant_count,
      r.created_at ? r.created_at.slice(0, 10) : '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'));
  }
  // BOM så Excel öppnar UTF-8 korrekt
  const blob = new Blob(['﻿' + csv.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `plattformsanalytics-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── PDF-export (window.print med dedikerad CSS-klass) ─────────────────────────

function printPdf() {
  window.print();
}

// ── KPI-kort ──────────────────────────────────────────────────────────────────

const KpiCard = ({ icon: Icon, label, value, sublabel, testId }) => (
  <div
    className="bg-white rounded border p-5"
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{label}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
        {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
      </div>
      <Icon size={24} style={{ color: '#9ca3af' }} />
    </div>
  </div>
);

// ── Huvudsida ────────────────────────────────────────────────────────────────

const PlatformAnalyticsPage = () => {
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('monthly_price');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, gr, cu] = await Promise.all([
        api.get('/admin/platform-analytics/overview'),
        api.get('/admin/platform-analytics/growth?months=6'),
        api.get('/admin/platform-analytics/customers-detail'),
      ]);
      setOverview(ov.data);
      setGrowth(Array.isArray(gr.data) ? gr.data : []);
      setCustomers(Array.isArray(cu.data) ? cu.data : []);
    } catch (err) {
      toast.error('Kunde inte hämta plattformsanalytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sortedCustomers = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...customers].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), 'sv') * dir;
    });
  }, [customers, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const growthChart = useMemo(() => growth.map((r) => ({
    ...r,
    label: r.month, // YYYY-MM
  })), [growth]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="platform-analytics-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 size={24} />
              Plattformsanalytics
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Aggregerad vy över hela AIUS Control-plattformen — tillväxt, intäkter, kundstatus.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => downloadCsv(sortedCustomers)}
              data-testid="export-csv-btn"
              className="flex items-center gap-1 px-3 py-2 border rounded text-sm hover:bg-gray-50"
            >
              <Download size={14} /> Exportera CSV
            </button>
            <button
              onClick={printPdf}
              data-testid="export-pdf-btn"
              className="flex items-center gap-1 px-3 py-2 rounded font-semibold text-black"
              style={{ background: '#F9CF3F' }}
            >
              <Download size={14} /> Exportera PDF
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Laddar plattformsdata...</p>
        ) : (
          <>
            {/* KPI-kort */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-grid">
              <KpiCard
                icon={Building2}
                label="Kunder"
                value={formatInt(overview?.total_customers)}
                sublabel={`${formatInt(overview?.active_customers)} aktiva`}
                testId="kpi-customers"
              />
              <KpiCard
                icon={TrendingUp}
                label="MRR"
                value={formatSek(overview?.mrr)}
                sublabel="Återk. månadsintäkt"
                testId="kpi-mrr"
              />
              <KpiCard
                icon={Activity}
                label="ARR"
                value={formatSek(overview?.arr)}
                sublabel="MRR × 12"
                testId="kpi-arr"
              />
              <KpiCard
                icon={Users}
                label="Användare"
                value={formatInt(overview?.total_users)}
                sublabel="Exkl. super_admin"
                testId="kpi-users"
              />
              <KpiCard
                icon={Target}
                label="Deltagare"
                value={formatInt(overview?.total_participants)}
                sublabel="Aktiva + pågående"
                testId="kpi-participants"
              />
              <KpiCard
                icon={FileText}
                label="Aktiva avtal"
                value={formatInt(overview?.active_contracts)}
                testId="kpi-contracts"
              />
            </div>

            {/* MRR-graf */}
            <section className="bg-white rounded border p-5" data-testid="mrr-chart-section">
              <h2 className="font-bold mb-3">MRR — senaste 6 månaderna</h2>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={growthChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => formatSek(v)} />
                  <Line
                    type="monotone"
                    dataKey="mrr_at_end"
                    name="MRR"
                    stroke="#F9CF3F"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </section>

            {/* Tillväxt-graf */}
            <section className="bg-white rounded border p-5" data-testid="growth-chart-section">
              <h2 className="font-bold mb-3">Kundtillväxt — nya vs churnade per månad</h2>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={growthChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" fontSize={12} />
                  <YAxis fontSize={12} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="new_customers" name="Nya kunder" fill="#16a34a" />
                  <Bar dataKey="churned_customers" name="Churnade kunder" fill="#dc2626" />
                </BarChart>
              </ResponsiveContainer>
            </section>

            {/* Kundtabell */}
            <section className="bg-white rounded border p-5" data-testid="customers-table-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold">Kunder per plan + MRR-bidrag</h2>
                <span className="text-xs text-gray-500">{customers.length} totalt</span>
              </div>
              {customers.length === 0 ? (
                <p className="text-sm text-gray-500">Inga kunder ännu.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="customers-table">
                    <thead className="text-left bg-gray-50">
                      <tr>
                        <Th onClick={() => toggleSort('name')}>
                          <span className="flex items-center gap-1">Kund {sortIcon('name')}</span>
                        </Th>
                        <Th onClick={() => toggleSort('contract_package')}>
                          <span className="flex items-center gap-1">Plan {sortIcon('contract_package')}</span>
                        </Th>
                        <Th onClick={() => toggleSort('monthly_price')}>
                          <span className="flex items-center gap-1">MRR {sortIcon('monthly_price')}</span>
                        </Th>
                        <Th onClick={() => toggleSort('user_count')}>
                          <span className="flex items-center gap-1">Användare {sortIcon('user_count')}</span>
                        </Th>
                        <Th onClick={() => toggleSort('participant_count')}>
                          <span className="flex items-center gap-1">Deltagare {sortIcon('participant_count')}</span>
                        </Th>
                        <Th>Status</Th>
                        <Th>Avtal t.o.m.</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCustomers.map((c) => {
                        const badge = CONTRACT_STATUS_BADGE[c.contract_status] || null;
                        return (
                          <tr key={c.customer_id} className="border-t" data-testid={`customer-row-${c.customer_id}`}>
                            <td className="px-3 py-2">
                              <p className="font-semibold">{c.name}</p>
                              <p className="text-xs text-gray-500 font-mono">{c.org_number || '—'}</p>
                            </td>
                            <td className="px-3 py-2">
                              {c.contract_package ? PLAN_LABEL[c.contract_package] || c.contract_package : '—'}
                            </td>
                            <td className="px-3 py-2 font-mono">{formatSek(c.monthly_price)}</td>
                            <td className="px-3 py-2">{formatInt(c.user_count)}</td>
                            <td className="px-3 py-2">{formatInt(c.participant_count)}</td>
                            <td className="px-3 py-2">
                              {badge ? (
                                <span
                                  className="px-2 py-0.5 rounded text-xs font-semibold"
                                  style={{ background: badge.bg, color: badge.color }}
                                >
                                  {badge.text}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">Inget avtal</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-600">{formatDate(c.contract_end)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

const Th = ({ children, onClick }) => (
  <th
    className={`px-3 py-2 font-semibold ${onClick ? 'cursor-pointer select-none hover:bg-gray-100' : ''}`}
    onClick={onClick}
  >
    {children}
  </th>
);

export default PlatformAnalyticsPage;
